// M3 (Postwar & Offshore, 1946-1999) region content: three new regions, 1946-1999 risk keyframes, and the
// political-economy tables of the era — nationalizations (seize / participation / service contract), host-government
// fiscal take, local partners. Merged into REGIONS by src/data/regions.js (new regions + risk keyframes only).
// The policy tables are NOT merged into the M2 tables (REGION_CLOSURES etc.), so the M2 politics code keeps its
// behaviour in games that run past 1946; the M3 sim reads them through the helpers below (docs/M3_SPEC.md §7-§8).
// Pure data + pure helpers; no imports from regions.js (avoids an import cycle).
//
// M2 safety rule (enforced by mergeM3RiskKeyframes, tested): values of regionRisk(id, year <= 1946) never change.
// If the nearest existing keyframe before an M3 keyframe is earlier than 1946, a "pin" keyframe equal to the
// existing interpolated value at 1946.0 is inserted first, so the 1945 curve is untouched.
// Fractional years mark the month (1951.33 ≈ 1 May 1951). Sources: Yergin, "The Prize" (1991); EIA country briefs;
// OPEC & national oil company histories. Values are judgement calls on a 0-1 scale.

const r = (year, stability, security, expropriation, corruption, extra = {}) => ({ year, stability, security, expropriation, corruption, ...extra });
const RISK_KEYS = ['stability', 'security', 'expropriation', 'corruption'];
export const M3_FROM_YEAR = 1946;

// ------------------------------------------------------------------ new regions
export const M3_REGIONS = {
  algeria: {
    id: 'algeria', name: 'Algeria (Sahara)', country: 'Algeria', continent: 'africa', currency: 'USD',
    lat: 31.67, lon: 6.07, opensYear: 1953, leaseMult: 0.35, royalty: 0.125, transportToMarket: 0.5,
    hazards: ['sabotage', 'uprising', 'kidnapping', 'expropriation'],
    risk: [r(1953, 0.5, 0.5, 0.05, 0.5), r(1954.84, 0.3, 0.3, 0.08, 0.5), // FLN uprising, 1 Nov 1954
           r(1958, 0.2, 0.25, 0.1, 0.5), r(1961.3, 0.15, 0.2, 0.15, 0.5), // OAS terror
           r(1962.5, 0.35, 0.45, 0.3, 0.6), // independence; Évian guarantees French oil rights
           r(1963.95, 0.5, 0.55, 0.4, 0.6), // Sonatrach founded 31 Dec 1963
           r(1971.15, 0.6, 0.6, 0.99, 0.6), // 51% nationalization of French concessions, 24 Feb 1971
           r(1972, 0.6, 0.6, 0.5, 0.6), r(1986.6, 0.5, 0.55, 0.2, 0.65), // Law 86-14 reopens the upstream
           r(1992.03, 0.2, 0.3, 0.25, 0.7), // election cancelled, civil war
           r(1994, 0.1, 0.15, 0.2, 0.75), r(1997.6, 0.1, 0.12, 0.15, 0.75), r(1999.3, 0.3, 0.35, 0.12, 0.7), r(2025, 0.5, 0.45, 0.1, 0.65)],
    blurb: 'Hassi Messaoud under the Grand Erg Oriental. A French concession in a colonial war, then Sonatrach’s.',
  },
  west_siberia: {
    id: 'west_siberia', name: 'West Siberia (Russia)', country: 'Soviet Union / Russia', continent: 'asia', currency: 'USD',
    lat: 61.13, lon: 76.75, opensYear: 1991, leaseMult: 0.3, royalty: 0.2, transportToMarket: 2.0,
    hazards: ['cold', 'expropriation', 'theft', 'sanctions'],
    risk: [r(1965, 0.6, 0.9, 0.99, 0.6), r(1991, 0.35, 0.6, 0.35, 0.8),
           r(1991.98, 0.25, 0.55, 0.4, 0.85), // the USSR dissolves
           r(1993.78, 0.2, 0.5, 0.4, 0.85), // constitutional crisis
           r(1995.9, 0.3, 0.5, 0.35, 0.9), // loans-for-shares
           r(1998.63, 0.15, 0.45, 0.5, 0.9), // default of 17 Aug 1998
           r(1999.9, 0.3, 0.5, 0.45, 0.9), r(2025, 0.35, 0.6, 0.6, 0.85)],
    blurb: 'Samotlor and the bogs of the Ob. Closed to foreigners for decades; then joint ventures, oligarchs and shifting rules.',
  },
  kazakhstan: {
    id: 'kazakhstan', name: 'Kazakhstan (Pre-Caspian)', country: 'Kazakhstan', continent: 'asia', currency: 'USD',
    lat: 46.15, lon: 53.38, opensYear: 1993, leaseMult: 0.35, royalty: 0.2, transportToMarket: 2.2,
    hazards: ['cold', 'expropriation', 'theft'],
    risk: [r(1979, 0.6, 0.9, 0.99, 0.6), r(1991.95, 0.5, 0.7, 0.3, 0.8), // independence 16 Dec 1991
           r(1993.26, 0.6, 0.75, 0.2, 0.8), r(1998.6, 0.5, 0.7, 0.25, 0.85), r(2025, 0.6, 0.75, 0.2, 0.8)],
    blurb: 'A landlocked giant on the Caspian steppe. Every barrel leaves through Russia, on Russia’s terms.',
  },
};

// ------------------------------------------------------------------ extra keyframes (1946-1999 detail)
// `override: true` replaces an existing keyframe at the same year (only allowed at >= 1946, and only when the
// keyframe before it is also >= 1946, so nothing before 1946 moves).
export const M3_RISK_KEYFRAMES = {
  saudi: [
    r(1950.95, 0.8, 0.8, 0.05, 0.5),   // fifty-fifty with Aramco, 30 Dec 1950
    r(1953.8, 0.75, 0.75, 0.05, 0.5),  // Aramco labour strike, Oct 1953
    r(1962.8, 0.7, 0.75, 0.08, 0.55),  // Yemen war on the border
    r(1967.43, 0.6, 0.65, 0.15, 0.55), // Six-Day War, embargo, riots at Dhahran
    r(1968, 0.75, 0.8, 0.15, 0.5),
    r(1972.95, 0.75, 0.8, 0.3, 0.5),   // General Agreement on Participation
    r(1974, 0.8, 0.8, 0.6, 0.5),       // 60% participation
    r(1976.5, 0.8, 0.8, 0.85, 0.5),
    r(1979.88, 0.55, 0.6, 0.95, 0.5),  // Grand Mosque seizure, Nov 1979
    r(1987.6, 0.6, 0.6, 0.95, 0.5),    // tanker war
    r(1990.59, 0.5, 0.5, 0.9, 0.5),    // Iraq on the border
    r(1991.3, 0.75, 0.75, 0.9, 0.5),
    r(1996.48, 0.65, 0.6, 0.85, 0.5),  // Khobar Towers
  ],
  kuwait: [
    r(1951.95, 0.8, 0.8, 0.05, 0.5),   // fifty-fifty, Dec 1951
    r(1961.47, 0.6, 0.55, 0.1, 0.5),   // independence; Qasim claims Kuwait, British troops land
    r(1962, 0.8, 0.8, 0.1, 0.5),
    r(1974, 0.8, 0.8, 0.6, 0.5),       // 60% participation
    r(1987.5, 0.6, 0.5, 0.9, 0.5),     // tankers reflagged under US escort
    r(1989.9, 0.7, 0.7, 0.9, 0.5),
    r(1990, 0.65, 0.65, 0.9, 0.5, { override: true }), // the base 1990 keyframe marked the invasion seven months early
    r(1990.58, 0.02, 0.01, 0.99, 0.5), // Iraqi invasion, 2 Aug 1990
    r(1991.15, 0.2, 0.15, 0.6, 0.5),   // liberation, 26 Feb 1991; 700 wells on fire
  ],
  persia: [
    r(1951, 0.25, 0.45, 0.6, 0.7, { override: true }),
    r(1951.33, 0.15, 0.35, 0.99, 0.7), // Mossadegh nationalizes Anglo-Iranian, 1 May 1951
    r(1953.63, 0.1, 0.3, 0.6, 0.7),    // coup of 19 Aug 1953
    r(1954.8, 0.55, 0.6, 0.1, 0.7),    // Consortium Agreement, Oct 1954
    r(1963.5, 0.5, 0.55, 0.08, 0.7),   // White Revolution, June 1963 riots
    r(1973.2, 0.6, 0.6, 0.5, 0.7),     // NIOC takes ownership; consortium becomes a contractor
    r(1978.7, 0.15, 0.25, 0.6, 0.75),  // Black Friday, oil-field strikes
    r(1980.72, 0.05, 0.05, 0.99, 0.8), // Iraq invades, Abadan besieged
    r(1988.63, 0.3, 0.3, 0.95, 0.8),   // ceasefire
    r(1995.35, 0.35, 0.4, 0.9, 0.8),   // US sanctions (EO 12959)
  ],
  iraq: [
    r(1952.1, 0.5, 0.5, 0.06, 0.65),   // IPC fifty-fifty, Feb 1952
    r(1958, 0.45, 0.5, 0.08, 0.65, { override: true }),
    r(1958.54, 0.15, 0.3, 0.3, 0.7),   // 14 July revolution
    r(1961.95, 0.3, 0.4, 0.5, 0.7),    // Law 80 takes 99.5% of IPC's concession area
    r(1963.1, 0.15, 0.3, 0.4, 0.7),    // Ramadan coup
    r(1968.55, 0.3, 0.4, 0.6, 0.7),    // Ba'ath coup
    r(1972, 0.35, 0.45, 0.8, 0.7, { override: true }),
    r(1972.42, 0.35, 0.45, 0.99, 0.7), // IPC nationalized, 1 June 1972
    r(1974.3, 0.4, 0.35, 0.95, 0.7),   // Kurdish war
    r(1980.72, 0.05, 0.05, 0.99, 0.8), // war with Iran
    r(1988.65, 0.25, 0.3, 0.95, 0.8),
    r(1990.58, 0.05, 0.05, 0.99, 0.85), // invasion of Kuwait; UN embargo
    r(1991.05, 0.02, 0.02, 0.99, 0.85), // Desert Storm
    r(1991.5, 0.15, 0.2, 0.99, 0.85),
    r(1996.95, 0.2, 0.25, 0.95, 0.85), // oil-for-food begins
  ],
  venezuela: [
    r(1948.9, 0.35, 0.5, 0.06, 0.75),  // Gallegos overthrown, Nov 1948
    r(1956.5, 0.45, 0.6, 0.04, 0.8),   // Pérez Jiménez's last concession round
    r(1958.06, 0.3, 0.45, 0.08, 0.7),  // dictatorship falls, 23 Jan 1958
    r(1958.95, 0.5, 0.55, 0.12, 0.65), // tax raised toward 60/40
    r(1960.7, 0.55, 0.6, 0.15, 0.6),   // OPEC; "no more concessions"
    r(1971.55, 0.6, 0.6, 0.5, 0.6),    // Hydrocarbons Reversion Law
    r(1975.65, 0.6, 0.65, 0.9, 0.6),   // nationalization law, 29 Aug 1975
    r(1976.5, 0.6, 0.65, 0.4, 0.6),
    r(1982, 0.55, 0.6, 0.15, 0.65),
    r(1983.13, 0.5, 0.55, 0.15, 0.65), // Black Friday devaluation
    r(1989.15, 0.3, 0.4, 0.15, 0.7),   // Caracazo riots
    r(1992.09, 0.25, 0.4, 0.1, 0.7),   // Chávez coup attempt, 4 Feb 1992
    r(1993.5, 0.4, 0.5, 0.08, 0.7),    // Apertura Petrolera
    r(1999.09, 0.35, 0.45, 0.25, 0.75), // Chávez inaugurated
  ],
  libya: [
    r(1959.5, 0.65, 0.7, 0.05, 0.6),
    r(1967.43, 0.45, 0.55, 0.12, 0.65), // riots against Western oil men after the Six-Day War
    r(1968, 0.6, 0.65, 0.1, 0.6),
    r(1969, 0.55, 0.65, 0.1, 0.6, { override: true }),
    r(1969.67, 0.3, 0.5, 0.4, 0.7),    // Gaddafi's coup, 1 Sept 1969
    r(1970.7, 0.35, 0.55, 0.6, 0.7),   // Occidental forced to concede higher prices
    r(1971.93, 0.4, 0.55, 0.8, 0.7),   // BP nationalized, Dec 1971
    r(1973, 0.45, 0.55, 0.85, 0.7, { override: true }),
    r(1973.67, 0.45, 0.55, 0.99, 0.7), // 51% nationalization, 1 Sept 1973
    r(1974.5, 0.5, 0.55, 0.6, 0.7),
    r(1981.92, 0.35, 0.45, 0.9, 0.75), // US travel ban, then import embargo (1982)
    r(1986.29, 0.2, 0.3, 0.95, 0.75),  // US strikes; US companies ordered out, June 1986
    r(1992.25, 0.3, 0.4, 0.6, 0.8),    // UN sanctions (Lockerbie)
    r(1999.27, 0.4, 0.45, 0.4, 0.8),   // suspects handed over, sanctions suspended
  ],
  nigeria: [
    r(1960.75, 0.65, 0.6, 0.05, 0.7),  // independence, 1 Oct 1960
    r(1966.04, 0.35, 0.4, 0.08, 0.75), // January 1966 coup
    r(1967, 0.3, 0.35, 0.1, 0.75, { override: true }),
    r(1967.5, 0.05, 0.05, 0.25, 0.8),  // Biafra secedes; civil war from 6 July 1967
    r(1970.04, 0.35, 0.35, 0.25, 0.8), // war ends, 15 Jan 1970
    r(1974.3, 0.55, 0.55, 0.4, 0.85),  // 55% participation
    r(1975.57, 0.4, 0.45, 0.35, 0.85), // Gowon overthrown
    r(1979.58, 0.5, 0.5, 0.5, 0.85),   // BP's Nigerian assets nationalized
    r(1983.99, 0.3, 0.4, 0.2, 0.9),    // Buhari coup
    r(1993.5, 0.2, 0.3, 0.15, 0.9),    // election annulled; Abacha
    r(1995, 0.2, 0.25, 0.1, 0.9, { override: true }),
    r(1995.86, 0.15, 0.2, 0.12, 0.9),  // Ken Saro-Wiwa executed
    r(1998.45, 0.3, 0.3, 0.1, 0.9),    // Abacha dies
    r(1999.4, 0.4, 0.3, 0.1, 0.9),     // civilian rule
    r(1999.88, 0.35, 0.2, 0.1, 0.9),   // Odi
  ],
  north_sea_uk: [
    r(1974.2, 0.85, 0.95, 0.03, 0.1),  // Labour government; BNOC participation talks
    r(1976, 0.9, 0.97, 0.05, 0.1),
    r(1979.4, 0.95, 0.97, 0.01, 0.1),
  ],
  north_sea_norway: [
    r(1972.5, 0.95, 0.98, 0.03, 0.05), // Statoil founded; state participation in new licences
  ],
  alberta: [
    r(1980.82, 0.8, 0.95, 0.05, 0.1),  // National Energy Program, 28 Oct 1980
    r(1985.25, 0.95, 0.97, 0, 0.05),   // Western Accord dismantles the NEP
  ],
};

// Merge extra keyframes into a sorted base list (returns a NEW array). Inserts a pin at 1946.0 when needed so no
// value before 1946 changes; supports { override: true } at >= 1946. Throws on unsafe inserts or duplicate years.
export function mergeM3RiskKeyframes(base, extra, regionId = '?') {
  if (!extra?.length) return base.slice();
  const interp = (list, y) => {
    if (y <= list[0].year) return list[0];
    if (y >= list[list.length - 1].year) return list[list.length - 1];
    for (let i = 1; i < list.length; i++) if (y <= list[i].year) {
      const a = list[i - 1], b = list[i], t = (y - a.year) / (b.year - a.year);
      return Object.fromEntries(RISK_KEYS.map((k) => [k, a[k] + (b[k] - a[k]) * t]));
    }
    return list[list.length - 1];
  };
  let out = base.map((k) => ({ ...k }));
  const minExtra = Math.min(...extra.map((k) => k.year));
  if (minExtra < M3_FROM_YEAR) throw new Error(`${regionId}: M3 keyframe ${minExtra} is before ${M3_FROM_YEAR}`);
  const prevOfFirst = out.filter((b) => b.year < minExtra).reduce((m, b) => Math.max(m, b.year), -Infinity);
  if (prevOfFirst < M3_FROM_YEAR && prevOfFirst > -Infinity && !out.some((b) => b.year === M3_FROM_YEAR)) {
    const v = interp(out, M3_FROM_YEAR);
    out.push({ year: M3_FROM_YEAR, ...Object.fromEntries(RISK_KEYS.map((k) => [k, v[k]])), pin: true });
    out.sort((a, b) => a.year - b.year);
  }
  for (const k of extra) {
    const { override, ...kf } = k;
    const i = out.findIndex((b) => b.year === kf.year);
    if (i >= 0) {
      if (!override) throw new Error(`${regionId}: duplicate keyframe year ${kf.year}`);
      const prev = out.filter((b) => b.year < kf.year).reduce((m, b) => Math.max(m, b.year), -Infinity);
      if (!(prev >= M3_FROM_YEAR)) throw new Error(`${regionId}: override at ${kf.year} would change pre-1946 risk`);
      out[i] = { ...kf, overridden: true };
      continue;
    }
    if (override) throw new Error(`${regionId}: override at ${kf.year} has nothing to replace`);
    const prev = out.filter((b) => b.year < kf.year).reduce((m, b) => Math.max(m, b.year), -Infinity);
    if (!(prev >= M3_FROM_YEAR) && prev > -Infinity) throw new Error(`${regionId}: M3 keyframe ${kf.year} would change pre-1946 risk (previous ${prev})`);
    out.push(kf);
    out.sort((a, b) => a.year - b.year);
  }
  return out;
}

// ------------------------------------------------------------------ nationalizations (M3_SPEC §7)
// mode 'seize'           : everything the player holds in the region passes to the state; `compensation` × book value
//                          paid after `compensationDays`; the region is closed until `until`.
// mode 'participation'   : the state takes `share` of equity: the player keeps (1 − share) of production and costs,
//                          receives `compensation` × book value of the share taken, and may buy back state crude at
//                          `buybackDiscount` below spot (the 1972 Riyadh General Agreement model).
// mode 'serviceContract' : the state owns the oil; the player keeps operating its wells for a fee per barrel
//                          (`feePerBbl`, base-year $) instead of the netback, plus `compensation` once.
// appliesTo: 'all' (default) | 'us' (US-domiciled companies: Libya 1986 sanctions) | 'newLicences' (Norway).
export const NATIONALIZATIONS = [
  { id: 'iran_1951', regionId: 'persia', from: 1951.33, until: 1954.83, mode: 'seize', compensation: 0.25, compensationDays: 1250,
    reason: 'Mossadegh nationalizes Anglo-Iranian; British blockade and boycott of Iranian crude (the Abadan crisis)' },
  { id: 'iran_1973', regionId: 'persia', from: 1973.2, until: 1979.12, mode: 'serviceContract', feePerBbl: 0.22, baseYear: 1973, compensation: 0,
    reason: 'Sales and Purchase Agreement: NIOC owns the fields, the consortium operates them for a fee' },
  { id: 'iran_1979', regionId: 'persia', from: 1979.12, until: 2100, mode: 'seize', compensation: 0.05, compensationDays: 2000,
    reason: 'Islamic Revolution: all foreign contracts cancelled' },
  { id: 'iraq_1972', regionId: 'iraq', from: 1972.42, until: 2009.5, mode: 'seize', compensation: 0.15, compensationDays: 300,
    reason: 'Law 69: the Iraq Petroleum Company is nationalized, 1 June 1972' },
  { id: 'algeria_1971', regionId: 'algeria', from: 1971.15, until: 2100, mode: 'participation', share: 0.51, compensation: 0.25, buybackDiscount: 0.05,
    reason: 'Boumediène takes 51% of the French concessions and all of the gas, 24 Feb 1971' },
  { id: 'libya_1973', regionId: 'libya', from: 1973.67, until: 2100, mode: 'participation', share: 0.51, compensation: 0.2, buybackDiscount: 0.03,
    reason: 'Revolutionary Command Council decree: 51% of every concession, 1 Sept 1973' },
  { id: 'libya_us_1986', regionId: 'libya', from: 1986.5, until: 2004.5, mode: 'seize', compensation: 0, compensationDays: 0, appliesTo: 'us',
    reason: 'US sanctions: American companies must leave Libya; the NOC holds their share "in trust"' },
  { id: 'saudi_1973', regionId: 'saudi', from: 1973.0, until: 1974.0, mode: 'participation', share: 0.25, compensation: 0.3, buybackDiscount: 0.05,
    reason: 'General Agreement on Participation: 25% state share in Aramco' },
  { id: 'saudi_1974', regionId: 'saudi', from: 1974.0, until: 1980.0, mode: 'participation', share: 0.6, compensation: 0.3, buybackDiscount: 0.05,
    reason: '60% state participation in Aramco' },
  { id: 'saudi_1980', regionId: 'saudi', from: 1980.0, until: 1988.87, mode: 'serviceContract', feePerBbl: 0.21, baseYear: 1980, compensation: 0.3,
    reason: 'Full takeover of Aramco, settled on net book value; the American partners stay on as operators for a fee' },
  { id: 'saudi_1988', regionId: 'saudi', from: 1988.87, until: 2100, mode: 'seize', compensation: 0, compensationDays: 0,
    reason: 'Saudi Aramco is incorporated and takes over operations, Nov 1988' },
  { id: 'kuwait_1974', regionId: 'kuwait', from: 1974.0, until: 1975.92, mode: 'participation', share: 0.6, compensation: 0.3, buybackDiscount: 0.05,
    reason: '60% participation in the Kuwait Oil Company' },
  { id: 'kuwait_1975', regionId: 'kuwait', from: 1975.92, until: 2100, mode: 'seize', compensation: 0.2, compensationDays: 120,
    reason: 'Kuwait takes 100% of KOC; BP and Gulf keep only discounted supply contracts' },
  { id: 'bahrain_1974', regionId: 'bahrain', from: 1974.0, until: 1980.0, mode: 'participation', share: 0.6, compensation: 0.3, buybackDiscount: 0.05,
    reason: '60% state share in BAPCO’s producing operations' },
  { id: 'bahrain_1980', regionId: 'bahrain', from: 1980.0, until: 2100, mode: 'seize', compensation: 0.3, compensationDays: 365,
    reason: 'Bahrain completes the takeover of BAPCO' },
  { id: 'venezuela_1976', regionId: 'venezuela', from: 1976.0, until: 1992.5, mode: 'serviceContract', feePerBbl: 0.18, baseYear: 1976, compensation: 0.3,
    reason: 'Nationalization, 1 Jan 1976: concessionaires become PDVSA affiliates paid a technical-assistance fee' },
  { id: 'nigeria_1971', regionId: 'nigeria', from: 1971.3, until: 1974.3, mode: 'participation', share: 0.35, compensation: 0.3, buybackDiscount: 0.04,
    reason: 'Nigeria joins OPEC; NNOC takes 35%' },
  { id: 'nigeria_1974', regionId: 'nigeria', from: 1974.3, until: 1979.5, mode: 'participation', share: 0.55, compensation: 0.3, buybackDiscount: 0.04,
    reason: '55% state participation' },
  { id: 'nigeria_1979', regionId: 'nigeria', from: 1979.5, until: 2100, mode: 'participation', share: 0.6, compensation: 0.3, buybackDiscount: 0.04,
    reason: '60% NNPC participation (BP, alone, is nationalized outright)' },
  { id: 'norway_1972', regionId: 'north_sea_norway', from: 1972.5, until: 2100, mode: 'participation', share: 0.5, compensation: 0, buybackDiscount: 0, appliesTo: 'newLicences',
    reason: 'Statoil takes a carried 50% (often more) of every new licence' },
];

// Active nationalization records for region at fractional year (optionally filtered for the player's situation).
export function nationalizationsAt(regionId, year, { usCompany = true, licenceYear = null } = {}) {
  return NATIONALIZATIONS.filter((n) => n.regionId === regionId && year >= n.from && year < n.until
    && (n.appliesTo !== 'us' || usCompany)
    && (n.appliesTo !== 'newLicences' || (licenceYear != null && licenceYear >= n.from)));
}
// State equity share in force (0 when none). Seizures count as 1.
export function stateShareAt(regionId, year, opts = {}) {
  let s = 0;
  for (const n of nationalizationsAt(regionId, year, opts)) s = Math.max(s, n.mode === 'participation' ? n.share : 1);
  return s;
}
// Combined closure lookup: M2 closures (pass M2 REGION_CLOSURES in) plus M3 'seize' records.
export function regionClosureM3(regionId, year, m2Closures = {}, opts = {}) {
  const m2 = (m2Closures[regionId] ?? []).find((c) => year >= c.from && year < c.until);
  if (m2) return m2;
  const n = nationalizationsAt(regionId, year, opts).find((x) => x.mode === 'seize');
  return n ? { from: n.from, until: n.until, compensation: n.compensation, reason: n.reason, id: n.id } : null;
}

// ------------------------------------------------------------------ host-government fiscal take (M3_SPEC §8)
// Step schedules: the last entry with year <= y applies. Fields:
//   royalty          share of gross (on `basis` price)
//   incomeTax        host income tax on (basis price − royalty − costs) × volume; creditable against US tax
//   royaltyCredited  true: royalty counts toward the income tax (the original 1950 fifty-fifty); false: expensed (OPEC 1964)
//   basis            'posted' (tax reference price, can exceed what you sell for) | 'realized'
//   specialTax       extra ring-fenced field tax on profit (UK PRT, Norway special tax), before income tax
//   allowance        bbl/d per field free of specialTax (UK PRT oil allowance)
// Sources: Yergin (1991); Mommer, "Global Oil and the Nation State" (2002); HMRC PRT history; Norwegian Petroleum
// Directorate. Rates rounded; "take" before 1950 is the M2 royalty schedule.
export const GOVERNMENT_TAKE = {
  saudi:     [[1950.95, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'posted' }],
              [1965, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: false, basis: 'posted' }],
              [1971.1, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }],
              [1974.95, { royalty: 0.2, incomeTax: 0.85, royaltyCredited: false, basis: 'posted' }]],
  kuwait:    [[1951.95, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'posted' }],
              [1965, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: false, basis: 'posted' }],
              [1971.1, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }],
              [1974.95, { royalty: 0.2, incomeTax: 0.85, royaltyCredited: false, basis: 'posted' }]],
  iraq:      [[1952.1, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'posted' }],
              [1965, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: false, basis: 'posted' }],
              [1971.1, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }]],
  persia:    [[1954.83, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'posted' }],
              [1965, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: false, basis: 'posted' }],
              [1971.1, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }]],
  bahrain:   [[1952, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'posted' }],
              [1971.1, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }]],
  venezuela: [[1948.85, { royalty: 0.1667, incomeTax: 0.5, royaltyCredited: true, basis: 'realized' }], // the first fifty-fifty, Nov 1948
              [1958.95, { royalty: 0.1667, incomeTax: 0.6, royaltyCredited: true, basis: 'realized' }],
              [1966.9, { royalty: 0.1667, incomeTax: 0.52, royaltyCredited: false, basis: 'posted' }],    // tax reference prices
              [1970.95, { royalty: 0.1667, incomeTax: 0.6, royaltyCredited: false, basis: 'posted' }]],
  libya:     [[1955.5, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'realized' }],   // discounts allowed: Libya's lure
              [1965.9, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: false, basis: 'posted' }],
              [1970.7, { royalty: 0.125, incomeTax: 0.58, royaltyCredited: false, basis: 'posted' }],
              [1971.3, { royalty: 0.125, incomeTax: 0.6, royaltyCredited: false, basis: 'posted' }]],
  nigeria:   [[1959.3, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'realized' }],
              [1967, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: false, basis: 'posted' }],
              [1971.3, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }],
              [1975.2, { royalty: 0.2, incomeTax: 0.85, royaltyCredited: false, basis: 'posted' }]],
  algeria:   [[1958.9, { royalty: 0.125, incomeTax: 0.5, royaltyCredited: true, basis: 'realized' }],   // Saharan Petroleum Code
              [1965.6, { royalty: 0.125, incomeTax: 0.55, royaltyCredited: false, basis: 'posted' }],
              [1975, { royalty: 0.2, incomeTax: 0.85, royaltyCredited: false, basis: 'posted' }]],
  north_sea_uk:     [[1964.7, { royalty: 0.125, incomeTax: 0, basis: 'realized', specialTax: 0 }],
                     [1975.5, { royalty: 0.125, incomeTax: 0, basis: 'realized', specialTax: 0.45, allowance: 2700 }],   // Petroleum Revenue Tax
                     [1979.1, { royalty: 0.125, incomeTax: 0, basis: 'realized', specialTax: 0.6, allowance: 2700 }],
                     [1980.1, { royalty: 0.125, incomeTax: 0, basis: 'realized', specialTax: 0.7, allowance: 2700 }],
                     [1983.3, { royalty: 0.125, incomeTax: 0, basis: 'realized', specialTax: 0.75, allowance: 2700 }],
                     [1993.25, { royalty: 0.125, incomeTax: 0, basis: 'realized', specialTax: 0.5, allowance: 2700, newFieldsExempt: true }]],
  north_sea_norway: [[1965.6, { royalty: 0.12, incomeTax: 0, basis: 'realized', specialTax: 0 }],
                     [1975.5, { royalty: 0.12, incomeTax: 0, basis: 'realized', specialTax: 0.35 }],
                     [1980, { royalty: 0.12, incomeTax: 0, basis: 'realized', specialTax: 0.35 }],
                     [1992, { royalty: 0.08, incomeTax: 0, basis: 'realized', specialTax: 0.5 }]],
  alberta:          [[1973.5, { royalty: 0.25, incomeTax: 0, basis: 'realized', specialTax: 0 }],              // sliding provincial royalties after 1973
                     [1980.82, { royalty: 0.25, incomeTax: 0, basis: 'realized', specialTax: 0.12 }],           // NEP Petroleum and Gas Revenue Tax
                     [1986, { royalty: 0.2, incomeTax: 0, basis: 'realized', specialTax: 0 }]],
  west_siberia:     [[1991, { royalty: 0.2, incomeTax: 0.32, basis: 'realized', specialTax: 0.1, exportDutyPerBbl: 2 }]],
  kazakhstan:       [[1993.25, { royalty: 0.2, incomeTax: 0.3, basis: 'realized', specialTax: 0 }]],
  brazil:           [[1999, { royalty: 0.1, incomeTax: 0.34, basis: 'realized', specialTax: 0.1 }]],             // "participação especial"
};
const stepAt = (s, y) => { if (!s?.length || y < s[0][0]) return null; let v = s[0][1]; for (const [yy, x] of s) if (y >= yy) v = x; return v; };
// Fiscal terms for a region at fractional year, or null (use royalty + home taxes only).
export const governmentTake = (regionId, year) => stepAt(GOVERNMENT_TAKE[regionId], year);

// Effective government share of a barrel's margin (for UI and balance checks).
// price: what the player sells for; posted: the tax reference price; cost: $/bbl lifting cost.
export function hostTakePerBbl(terms, price, posted = price, cost = 0) {
  if (!terms) return 0;
  const basis = terms.basis === 'posted' ? posted : price;
  const roy = terms.royalty * basis;
  const special = (terms.specialTax ?? 0) * Math.max(0, price - roy - cost);
  // Fifty-fifty style: royalty is credited against the tax (take = max(royalty, tax on the whole margin));
  // OPEC 1964 "royalty expensing": royalty is a cost, tax on what is left, both paid.
  if (terms.royaltyCredited) return Math.max(roy, terms.incomeTax * Math.max(0, basis - cost - special)) + special;
  return roy + terms.incomeTax * Math.max(0, basis - roy - cost - special) + special;
}

// ------------------------------------------------------------------ local partners, M3 additions
export const M3_LOCAL_PARTNERS = {
  nigeria: [{ id: 'delta_chiefs', name: 'Community development agreements (Delta chiefs)', from: 1970, until: 2100, revenueShare: 0.03, attackMult: 0.6, expropriationMult: 1.0,
              desc: 'Schools, clinics and jobs for the creek villages, and a retainer for the chiefs. The youth groups were not consulted.' }],
  west_siberia: [{ id: 'regional_governor', name: 'Khanty-Mansiysk regional administration', from: 1991, until: 2100, revenueShare: 0.05, attackMult: 0.7, expropriationMult: 0.6,
              desc: 'A local partner with friends in Moscow, and a share of the JV.' }],
  kazakhstan: [{ id: 'kazmunaigas', name: 'State partner (Kazakhoil)', from: 1993.25, until: 2100, revenueShare: 0.08, attackMult: 0.8, expropriationMult: 0.5,
              desc: 'Bring the state company into your share and the export quota through Russia gets easier.' }],
  algeria: [{ id: 'sonatrach_partner', name: 'Sonatrach association', from: 1964, until: 2100, revenueShare: 0.1, attackMult: 0.8, expropriationMult: 0.4,
              desc: 'Associate with the state company before it associates with you.' }],
  venezuela: [{ id: 'pdvsa_apertura', name: 'PDVSA operating service agreement', from: 1992.5, until: 2007, revenueShare: 0.15, attackMult: 1, expropriationMult: 0.5,
              desc: 'The Apertura lets foreigners back in on marginal fields, with PDVSA as partner.' }],
};
